import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * SPA route-change tracker for Google Tag Manager.
 *
 * Pushes a `page_view` event to `window.dataLayer` on every navigation, so a
 * GA4 tag inside GTM can pick it up via a custom-event trigger.
 *
 *  - Normalises /results/:id → /results so reports aren't fragmented per
 *    valuation (the id is still captured as `valuation_id`).
 *  - Also emits a `valuation_completed` event when a user lands on
 *    a results page, so you can wire a conversion tag in GTM.
 *  - Dedupes consecutive identical page_paths (protects against React
 *    StrictMode double-firing in dev).
 *
 * GTM setup required (once, in the GTM UI):
 *   1. Variables → New → Data Layer Variable — create one for each of:
 *        page_path, page_title, page_location, valuation_id
 *   2. Triggers → New → Custom Event, Event name: `page_view`  → save as
 *      "CE - page_view".
 *   3. Tags → New → GA4 Configuration, Measurement ID `G-KTGGHF14FH`,
 *      trigger "All Pages", and UNCHECK "Send a page view event when this
 *      configuration loads".
 *   4. Tags → New → GA4 Event, config tag = the one above,
 *      Event name = `page_view`,
 *      Event parameters: page_path, page_title, page_location, valuation_id
 *      Trigger: CE - page_view.
 *   5. (Optional) Repeat step 4 for event name `valuation_completed` with
 *      a Custom Event trigger listening for `valuation_completed`.
 */
export default function GATracker() {
  const location = useLocation();
  const lastPathRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];

    let path = location.pathname;
    let title = document.title;
    let valuationId = null;

    const resultsMatch = path.match(/^\/results\/([^/]+)$/);
    if (resultsMatch) {
      valuationId = resultsMatch[1];
      path = "/results";
      title = "Valuation Results — GymSales.com.au";
    }

    const fullPath = path + location.search;
    if (lastPathRef.current === fullPath) return; // skip StrictMode duplicates
    lastPathRef.current = fullPath;

    window.dataLayer.push({
      event: "page_view",
      page_path: fullPath,
      page_title: title,
      page_location: window.location.origin + fullPath,
      valuation_id: valuationId,
    });

    if (resultsMatch) {
      window.dataLayer.push({
        event: "valuation_completed",
        valuation_id: valuationId,
      });
    }
  }, [location.pathname, location.search]);

  return null;
}
